import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';

const SOURCE_TYPE_LABELS = {
  authorized_api: 'Authorized API',
  licensed_dataset: 'Licensed Dataset',
  official_website: 'Official Website',
  partner: 'Partner Data',
  business_submission: 'Business Submission',
  manual: 'Manual Entry',
  csv: 'CSV Import',
  other: 'Other',
};

const STATUS_STYLES = {
  pending: 'bg-gray-100 text-gray-500',
  processing: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-400',
  undone: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS = {
  pending: 'Pending',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  undone: 'Undone',
};

const CSV_SAMPLE_HEADERS = 'business_name,category,city,district,address,pincode,phone,email,website';
const CSV_SAMPLE_ROW = 'Sunrise Medical Store,Medical Store,Mumbai,Mumbai City,12 MG Road,400001,9876543210,contact@sunrisemedical.com,https://sunrisemedical.com';

function downloadSampleCsv() {
  const content = `${CSV_SAMPLE_HEADERS}\n${CSV_SAMPLE_ROW}\n`;
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'sample_business_import.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function StatusBadge({ status }) {
  const key = status || 'pending';
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${STATUS_STYLES[key] || STATUS_STYLES.pending}`}>
      {STATUS_LABELS[key] || key}
    </span>
  );
}

function buildCategoryOptions(categories) {
  const byParent = new Map();
  categories.forEach((c) => {
    const key = c.parent_id || 'root';
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(c);
  });

  const flat = [];
  const walk = (parentKey, depth) => {
    const children = byParent.get(parentKey) || [];
    children.forEach((c) => {
      flat.push({ id: c.id, label: `${'— '.repeat(depth)}${c.name}` });
      walk(c.id, depth + 1);
    });
  };
  walk('root', 0);
  return flat;
}

function ErrorReport({ importId }) {
  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ last_page: 1, total: 0 });

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/business-imports/${importId}/errors`, { params: { page: p } });
      setErrors(data.data || []);
      setMeta({ last_page: data.last_page, total: data.total });
    } catch (err) {
      setErrors([]);
    } finally {
      setLoading(false);
    }
  }, [importId]);

  useEffect(() => { load(page); }, [load, page]);

  if (loading && errors === null) {
    return <div className="text-xs text-gray-400 py-4 text-center">Loading error report…</div>;
  }

  if (errors && errors.length === 0) {
    return <div className="text-xs text-gray-400 py-4 text-center">No row errors recorded.</div>;
  }

  return (
    <div className="mt-3 border-t pt-3">
      <div className="text-xs font-semibold text-gray-400 uppercase mb-2">
        Row Errors ({meta.total})
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#EFF6FF] text-[#0D1B3D]">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Row</th>
              <th className="text-left px-3 py-2 font-semibold">Error</th>
              <th className="text-left px-3 py-2 font-semibold">Reason</th>
              <th className="text-left px-3 py-2 font-semibold">Original Data</th>
            </tr>
          </thead>
          <tbody>
            {(errors || []).map((e) => (
              <tr key={e.id} className="border-t align-top">
                <td className="px-3 py-2 font-mono">{e.row_number}</td>
                <td className="px-3 py-2 text-red-600">{e.error}</td>
                <td className="px-3 py-2 text-gray-500">{e.reason || '—'}</td>
                <td className="px-3 py-2 font-mono text-gray-500 max-w-xs truncate">
                  {e.original_data ? JSON.stringify(e.original_data) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {meta.last_page > 1 && (
        <div className="flex items-center justify-center gap-3 mt-3">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-2.5 py-1 rounded-lg border border-gray-200 text-xs disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-xs text-gray-500">Page {page} of {meta.last_page}</span>
          <button
            onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
            disabled={page >= meta.last_page}
            className="px-2.5 py-1 rounded-lg border border-gray-200 text-xs disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function ImportProgressCard({ importItem, onUpdate }) {
  const [showErrors, setShowErrors] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [confirmingUndo, setConfirmingUndo] = useState(false);
  const intervalRef = useRef(null);

  const handleUndo = async () => {
    setUndoing(true);
    try {
      await api.post(`/admin/business-imports/${importItem.id}/undo`);
      onUpdate(importItem.id, { status: 'undone', successful_rows: 0, duplicate_rows: 0 });
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to undo import.');
    } finally {
      setUndoing(false);
      setConfirmingUndo(false);
    }
  };

  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await api.get(`/admin/business-imports/${importItem.id}`);
        onUpdate(importItem.id, data);
        if (['completed', 'failed', 'cancelled'].includes(data.status)) {
          clearInterval(intervalRef.current);
        }
      } catch (err) {
        clearInterval(intervalRef.current);
      }
    };

    if (!['completed', 'failed', 'cancelled'].includes(importItem.status)) {
      intervalRef.current = setInterval(poll, 2000);
      poll();
    }

    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importItem.id]);

  const pct = importItem.total_rows > 0
    ? Math.min(100, Math.round((importItem.processed_rows / importItem.total_rows) * 100))
    : 0;

  return (
    <div className="bg-white rounded-xl shadow p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-[#0D1B3D] truncate">{importItem.file_name}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {importItem.started_at ? `Started ${new Date(importItem.started_at).toLocaleString('en-IN')}` : 'Queued'}
          </p>
        </div>
        <StatusBadge status={importItem.status} />
      </div>

      <div className="mt-3">
        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
          <div
            className="h-2 bg-[#2563EB] transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {importItem.processed_rows} / {importItem.total_rows} rows processed ({pct}%)
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
        {[
          { label: 'Successful', value: importItem.successful_rows, color: 'text-[#10B981]' },
          { label: 'Duplicates', value: importItem.duplicate_rows, color: 'text-[#F59E0B]' },
          { label: 'Invalid', value: importItem.invalid_rows, color: 'text-red-500' },
          { label: 'Failed', value: importItem.failed_rows, color: 'text-red-500' },
        ].map((s) => (
          <div key={s.label} className="bg-gray-50 rounded-lg p-2 text-center">
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">{s.label}</p>
          </div>
        ))}
      </div>

      {(importItem.invalid_rows > 0 || importItem.failed_rows > 0) && (
        <button
          onClick={() => setShowErrors((v) => !v)}
          className="mt-3 text-xs font-semibold text-[#2563EB] hover:underline"
        >
          {showErrors ? 'Hide error report' : 'View error report'}
        </button>
      )}

      {showErrors && <ErrorReport importId={importItem.id} />}

      {importItem.status === 'completed' && (
        <div className="mt-3 pt-3 border-t">
          {confirmingUndo ? (
            <span className="inline-flex items-center gap-3">
              <span className="text-xs text-gray-500">Remove all businesses from this import?</span>
              <button
                onClick={handleUndo}
                disabled={undoing}
                className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
              >
                {undoing ? 'Undoing…' : 'Confirm Undo'}
              </button>
              <button
                onClick={() => setConfirmingUndo(false)}
                className="text-xs font-semibold text-gray-400 hover:underline"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              onClick={() => setConfirmingUndo(true)}
              className="text-xs font-semibold text-red-600 hover:underline"
            >
              Undo Import
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function LocationCategorySelects({ options, form, handleFieldChange, filteredDistricts, filteredCities, categoryOptions }) {
  return (
    <>
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">State</label>
        <select
          value={form.state_id}
          onChange={(e) => handleFieldChange('state_id', e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Select state (optional)</option>
          {options.states.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">District</label>
        <select
          value={form.district_id}
          onChange={(e) => handleFieldChange('district_id', e.target.value)}
          disabled={!form.state_id}
          className="w-full border rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-50"
        >
          <option value="">Select district (optional)</option>
          {filteredDistricts.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">City</label>
        <select
          value={form.city_id}
          onChange={(e) => handleFieldChange('city_id', e.target.value)}
          disabled={!form.district_id}
          className="w-full border rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-50"
        >
          <option value="">Select city (optional)</option>
          {filteredCities.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">Category</label>
        <select
          value={form.category_id}
          onChange={(e) => handleFieldChange('category_id', e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Select category (optional)</option>
          {categoryOptions.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
      </div>
    </>
  );
}

function CsvImportTab({ options, form, handleFieldChange, filteredDistricts, filteredCities, categoryOptions }) {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [imports, setImports] = useState([]);
  const fileInputRef = useRef(null);

  const handleFile = (f) => {
    if (f && (f.type === 'text/csv' || f.name.endsWith('.csv'))) {
      setFile(f);
      setFormError('');
    } else if (f) {
      setFormError('Please choose a .csv file.');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    handleFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!file) {
      setFormError('Please choose a CSV file to upload.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (form.state_id) fd.append('state_id', form.state_id);
      if (form.district_id) fd.append('district_id', form.district_id);
      if (form.city_id) fd.append('city_id', form.city_id);
      if (form.category_id) fd.append('category_id', form.category_id);
      fd.append('source_type', 'csv');
      fd.append('file', file);

      const { data } = await api.post('/admin/business-imports', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setImports((prev) => [
        {
          id: data.import_id,
          file_name: file.name,
          status: data.status,
          total_rows: 0,
          processed_rows: 0,
          successful_rows: 0,
          duplicate_rows: 0,
          invalid_rows: 0,
          failed_rows: 0,
          started_at: null,
        },
        ...prev,
      ]);

      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      const errors = err.response?.data?.errors;
      setFormError(
        errors ? Object.values(errors).flat().join(' ') : (err.response?.data?.message || 'Failed to start import.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleImportUpdate = (id, data) => {
    setImports((prev) => prev.map((imp) => (imp.id === id ? { ...imp, ...data } : imp)));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl shadow p-6">
          <h3 className="font-bold text-[#0D1B3D] mb-1">Upload CSV File</h3>
          <p className="text-xs text-gray-500 mb-4">Drop a file or browse — we'll match it against your location and category master data.</p>

          {formError && <p className="text-sm text-red-600 mb-3">{formError}</p>}

          <div
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${
              dragActive ? 'border-[#2563EB] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="w-10 h-10 rounded-full bg-[#EFF6FF] text-[#2563EB] flex items-center justify-center mx-auto mb-3 text-lg">
              ↑
            </div>
            {file ? (
              <p className="font-semibold text-[#0D1B3D]">{file.name}</p>
            ) : (
              <>
                <p className="font-semibold text-[#0D1B3D]">Drop your CSV here</p>
                <p className="text-xs text-gray-400 mt-1">or <span className="text-[#2563EB] underline">browse to upload</span></p>
              </>
            )}
            <span className="inline-block mt-3 text-[10px] font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-500">.CSV only</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => handleFile(e.target.files?.[0] || null)}
              className="hidden"
            />
          </div>

          <button
            type="button"
            onClick={downloadSampleCsv}
            className="w-full mt-4 border border-gray-200 rounded-lg py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50">
            ↓ Download Sample CSV
          </button>

          <button
            onClick={handleSubmit}
            disabled={submitting || !file}
            className="w-full mt-4 bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] text-white py-2.5 rounded-lg font-semibold text-sm hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? 'Starting import…' : 'Start Import'}
          </button>
        </div>

        <div className="bg-[#0D1B3D] rounded-xl shadow p-5 text-white h-fit">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center text-xs">{'</>'}</span>
            <h4 className="font-bold text-sm">CSV Format</h4>
          </div>
          <p className="text-[11px] text-white/50 mb-3">Follow this exact structure</p>
          <div className="bg-black/30 rounded-lg p-3 font-mono text-[11px] leading-relaxed overflow-x-auto">
            <p className="text-[#93C5FD]">{CSV_SAMPLE_HEADERS}</p>
            <p className="text-white/70 mt-2">{CSV_SAMPLE_ROW}</p>
          </div>
          <ul className="text-[11px] text-white/50 mt-3 space-y-1 list-disc list-inside">
            <li><span className="text-white/80">business_name</span>, <span className="text-white/80">city</span> and <span className="text-white/80">district</span> are required</li>
            <li><span className="text-white/80">city</span> must match an existing city</li>
            <li><span className="text-white/80">district</span> must match an existing district, and belong to the city above</li>
            <li><span className="text-white/80">category</span> must match an existing category</li>
          </ul>
        </div>
      </div>

      {imports.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
            This Session's Imports
          </h3>
          {imports.map((imp) => (
            <ImportProgressCard key={imp.id} importItem={imp} onUpdate={handleImportUpdate} />
          ))}
        </div>
      )}
    </div>
  );
}

function ManualEntryTab({ options, form, handleFieldChange, filteredDistricts, filteredCities, categoryOptions }) {
  const [manualForm, setManualForm] = useState({
    business_name: '', address: '', pincode: '', phone: '', email: '', website: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const handleManualChange = (field, value) => {
    setManualForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSuccessMsg('');

    if (!manualForm.business_name.trim()) {
      setFormError('Business name is required.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        business_name: manualForm.business_name,
        address: manualForm.address || null,
        pincode: manualForm.pincode || null,
        phone: manualForm.phone || null,
        email: manualForm.email || null,
        website: manualForm.website || null,
        state_id: form.state_id || null,
        district_id: form.district_id || null,
        city_id: form.city_id || null,
        category_id: form.category_id || null,
      };
      const { data } = await api.post('/admin/businesses', payload);
      setSuccessMsg(`"${data.business_name}" was added successfully.`);
      setManualForm({ business_name: '', address: '', pincode: '', phone: '', email: '', website: '' });
    } catch (err) {
      const errors = err.response?.data?.errors;
      setFormError(
        errors ? Object.values(errors).flat().join(' ') : (err.response?.data?.message || 'Failed to add business.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white rounded-xl shadow p-6">
        <h3 className="font-bold text-[#0D1B3D] mb-1">Add Business Manually</h3>
        <p className="text-xs text-gray-500 mb-4">Add one business at a time.</p>

        {formError && <p className="text-sm text-red-600 mb-3">{formError}</p>}
        {successMsg && <p className="text-sm text-[#10B981] mb-3">{successMsg}</p>}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Business Name *</label>
            <input
              type="text"
              placeholder="e.g. Sunrise Medical Store"
              value={manualForm.business_name}
              onChange={(e) => handleManualChange('business_name', e.target.value)}
              required
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <LocationCategorySelects
              options={options}
              form={form}
              handleFieldChange={handleFieldChange}
              filteredDistricts={filteredDistricts}
              filteredCities={filteredCities}
              categoryOptions={categoryOptions}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Address</label>
            <input
              type="text"
              placeholder="Street, area"
              value={manualForm.address}
              onChange={(e) => handleManualChange('address', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Pincode</label>
              <input
                type="text"
                placeholder="400001"
                value={manualForm.pincode}
                onChange={(e) => handleManualChange('pincode', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Phone</label>
              <input
                type="text"
                placeholder="9876543210"
                value={manualForm.phone}
                onChange={(e) => handleManualChange('phone', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Email</label>
              <input
                type="text"
                placeholder="contact@business.com"
                value={manualForm.email}
                onChange={(e) => handleManualChange('email', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Website</label>
              <input
                type="text"
                placeholder="https://business.com"
                value={manualForm.website}
                onChange={(e) => handleManualChange('website', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-[#7C3AED] to-[#4C1D95] text-white py-2.5 rounded-lg font-semibold text-sm hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? 'Adding…' : '+ Add Business'}
          </button>
        </form>
      </div>

      <div className="bg-[#F0FDF4] border border-green-100 rounded-xl shadow-sm p-5 h-fit">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-7 h-7 rounded-lg bg-[#10B981]/10 text-[#10B981] flex items-center justify-center text-xs">👤</span>
          <h4 className="font-bold text-sm text-[#0D1B3D]">Manual Entry Mode</h4>
        </div>
        <p className="text-[11px] text-gray-500">Add one business at a time. Fill in the details on the left and hit Add Business.</p>
      </div>
    </div>
  );
}

export default function BusinessImportPanel() {
  const [tab, setTab] = useState('csv');
  const [options, setOptions] = useState({ states: [], districts: [], cities: [], categories: [] });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState('');

  const [form, setForm] = useState({
    state_id: '', district_id: '', city_id: '', category_id: '',
  });

  useEffect(() => {
    api.get('/admin/business-imports/options')
      .then(({ data }) => setOptions(data))
      .catch(() => setOptionsError('Failed to load location/category options.'))
      .finally(() => setOptionsLoading(false));
  }, []);

  const filteredDistricts = options.districts.filter(
    (d) => String(d.state_id) === String(form.state_id)
  );
  const filteredCities = options.cities.filter(
    (c) => String(c.district_id) === String(form.district_id)
  );
  const categoryOptions = buildCategoryOptions(options.categories);

  const handleFieldChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'state_id') { next.district_id = ''; next.city_id = ''; }
      if (field === 'district_id') { next.city_id = ''; }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-gray-100">
        {[
          { key: 'csv', label: '↑ CSV' },
          { key: 'manual', label: '👤 Manual' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              tab === t.key
                ? 'border-[#2563EB] text-[#2563EB]'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {optionsError && <p className="text-sm text-red-600">{optionsError}</p>}

      {optionsLoading ? (
        <p className="text-sm text-gray-400">Loading form options…</p>
      ) : tab === 'csv' ? (
        <CsvImportTab
          options={options}
          form={form}
          handleFieldChange={handleFieldChange}
          filteredDistricts={filteredDistricts}
          filteredCities={filteredCities}
          categoryOptions={categoryOptions}
        />
      ) : (
        <ManualEntryTab
          options={options}
          form={form}
          handleFieldChange={handleFieldChange}
          filteredDistricts={filteredDistricts}
          filteredCities={filteredCities}
          categoryOptions={categoryOptions}
        />
      )}
    </div>
  );
}